import { createAsyncThunk, createAction } from '@reduxjs/toolkit';
import { isAuthenticate } from '../utils/api';
import {
  invalidateMetadataCache,
  clearPersistedCache,
  setCacheIdentity,
} from '../utils/metadataCache';

// Auth transitions must drop every cached response (dashboards, rosters,
// stores) — memory AND the persisted localStorage layer — so data cached
// under one identity is never served to another.
const clearCaches = (payload) => {
  invalidateMetadataCache();
  clearPersistedCache();
  setCacheIdentity('');
  return { payload };
};

// A failed session check means the local auth state can no longer be trusted.
// Clear it before leaving the current route so protected data is not restored
// from the previous session after the redirect.
export const redirectToLogin = () => {
  if (typeof window === 'undefined' || window.location.pathname === '/login') return;
  window.location.replace('/login');
};

export const authentication = createAsyncThunk('auth/authentication', async (_, { dispatch }) => {
  // Session identity may have just changed (login, impersonation end) —
  // never let responses cached under the previous identity survive.
  invalidateMetadataCache();
  let res;
  try {
    res = await isAuthenticate();
  } catch (error) {
    dispatch(logoutUser());
    redirectToLogin();
    throw error;
  }

  if (res?.success) {
    // Namespace the persisted dashboard cache by identity so numbers cached
    // under one login are never painted for another user or role.
    setCacheIdentity(`${res.role ?? ''}:${res.email ?? res.id ?? res.userId ?? ''}`);
    if (res.role === 'client' && res.token) {
      try {
        sessionStorage.setItem('skusavvy_client_token', res.token);
      } catch (e) {}
    }
    return {
      ...res,
      isAuthenticated: true,
    };
  }

  dispatch(logoutUser());
  redirectToLogin();
  return {
    isAuthenticated: false,
  };
});

export const logoutUser = createAction('auth/logout', clearCaches);
export const startImpersonation = createAction('auth/startImpersonation', clearCaches);
export const endImpersonationAction = createAction('auth/endImpersonation', clearCaches);
export const setViewMode = createAction('auth/setViewMode');
export const setStores = createAction('stores/setStores');
export const setSelectedStore = createAction('stores/setSelectedStore');
export const setClients = createAction('clients/setClients');
export const setSelectedClient = createAction('clients/setSelectedClient');
export const storePermanentlyRemoved = createAction('stores/permanentlyRemoved');
